import React, { useEffect, useRef } from 'react'
import {
  Container,
  Button,
  Row
  // Col,
  // Accordion,
  // AccordionContext
} from 'react-angle-dashboard-components'
import {
  ExpirationBanner,
  NotFoundCard,
  // SmallTitle,
  SmallBoldTitle,
  DataGrid,
  SubTitle
} from '../styles'
// import { IconUpArrow } from '../../CptSearch/vector'

import { ResultDetail } from './ResultDetail'
import { TicketForm } from '../TicketForm/TicketForm'
import { EligibilityResult, IGetEligibilityInfoReqBody } from '../../../services/eligibility'
import { DataCell } from './DataCell'

// import vectorIcon from './vector.png'
// import { BenefitsSection } from './BenefitsSection'

// Benefits code is commented out for future use after 1/1

interface SearchResultsProps {
  clearDataTime: string
  clearResults: (msg: string) => void
  criteriaData: IGetEligibilityInfoReqBody
  eligibilityResult: EligibilityResult | undefined
  formatForPrint: boolean
  hideAccums?: boolean
  selected: any
  toggleSelect: any
}

// const CaretIcon = () => {
//   const currentEventKey = useContext(AccordionContext)
//   return (
//     <img
//       className="pr-2"
//       style={{
//         ...(currentEventKey === '0' && {
//           transform: 'rotate(90deg) translate(4px, 7px)'
//         }),
//         height: 10
//       }}
//       src={vectorIcon}
//       alt="vector"
//     />
//   )
// }

// const mockBenefit = {
//   name: 'Family Planning',
//   subBenefits: [
//     'Diaphragm Fitting',
//     'Implanted Contraceptives',
//     'Injectable Contraceptives',
//     'Ora contraceptives and Diaphragms'
//   ]
// }

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0
})

export const SearchResults = React.forwardRef<any, SearchResultsProps>(
  (
    {
      clearDataTime,
      criteriaData,
      eligibilityResult,
      formatForPrint,
      hideAccums = false,
      selected,
      toggleSelect,
      clearResults
    },
    ref
  ) => {
    // const benefits = new Array(20).fill(mockBenefit)
    const scrollRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
      if (scrollRef && scrollRef.current && eligibilityResult) {
        scrollRef.current.scrollIntoView({ block: 'start', behavior: 'smooth' })
      }
    }, [scrollRef, eligibilityResult])

    if (!eligibilityResult) {
      return (
        <div>
          <Container ref={ref} className="mt-4">
            <NotFoundCard>
              <h5>
                We do not have a member on file that matches the information you
                entered
              </h5>
            </NotFoundCard>
            <TicketForm 
              clearResults={clearResults}
              criteriaData={criteriaData}
              foundResult={false} />
          </Container>
        </div>
      )
    }

    const benefitsUrl = eligibilityResult.plan?.sob_url

    let claimsAddress;
    
    switch (eligibilityResult.network) {
      case 'cigna':
      case 'cigna_ppo':
        claimsAddress = 'Payer ID# 62308\nPO Box 188061\nChattanooga, TN 37422-8061'
        break;
      default:
        claimsAddress = 'Payer ID# 39856\nPO BOX 21428\nEagan, MN 55121';
    }

    const {
      deductible_individual_inn,
      deductible_family_inn,
      deductible_individual_oon,
      deductible_family_oon,
      oop_individual_inn,
      oop_family_inn,
      oop_individual_oon,
      oop_family_oon,
      plan: {
        deductible_individual_inn: max_deductible_individual_inn,
        deductible_family_inn: max_deductible_family_inn,
        deductible_individual_oon: max_deductible_individual_oon,
        deductible_family_oon: max_deductible_family_oon,
        oop_max_individual_inn: max_oop_individual_inn,
        oop_max_family_inn: max_oop_family_inn,
        oop_max_individual_oon: max_oop_individual_oon,
        oop_max_family_oon: max_oop_family_oon
      } = {
        deductible_individual_inn: 0,
        deductible_family_inn: 0,
        deductible_individual_oon: 0,
        deductible_family_oon: 0,
        oop_max_individual_inn: 0,
        oop_max_family_inn: 0,
        oop_max_individual_oon: 0,
        oop_max_family_oon: 0
      }
    } = eligibilityResult

    const full_member_id = eligibilityResult.member.member_id
    const shortened_member_id = full_member_id.length === 9 || full_member_id.length === 12 ? full_member_id?.slice(0, -2) : full_member_id

    return (
      <div>
        <ExpirationBanner className="mb-4">
          <h5>
            All information on this page will be removed from the screen
            automatically at {clearDataTime} local time
          </h5>
        </ExpirationBanner>
        <Container ref={ref} className="mt-4">
          <Row
            ref={scrollRef}
            style={{
              scrollMarginTop: 250
            }}
          >
            <SmallBoldTitle>
              {shortened_member_id}
            </SmallBoldTitle>
            <div
              style={{
                marginLeft: 14,
                marginTop: 6,
                width: 71,
                height: 27,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                color: 'white',
                backgroundColor: eligibilityResult.active ? '#7DD491' : 'red',
                borderRadius: 4
              }}
            >
              {eligibilityResult.active ? 'Active' : 'Inactive'}
            </div>
          </Row>
          {eligibilityResult.active && (
            <>
              <Row>
                <ResultDetail
                  title="Coverage Effective Date"
                  content={eligibilityResult.effective_date}
                />
                <ResultDetail
                  title="Coverage End Date"
                  content={eligibilityResult.term_date}
                />
                <ResultDetail
                  title="Network"
                  content={
                    eligibilityResult.network
                      ?.toLowerCase()
                      ?.replace('_', ' ')
                      ?.split(' ')
                      ?.map((s) => {
                        if (s === 'ppo') {
                          return 'PPO'
                        }
                        return s.charAt(0).toUpperCase() + s.substring(1)
                      })
                      ?.join(' ') ?? 'Unknown'
                  }
                />
              </Row>
              <Row className="mt-3">
                <ResultDetail
                  title="Claims Mailing Address"
                  content={claimsAddress}
                />
                <ResultDetail
                  title="Return Check Address"
                  content={
                    'Angle Health\n650 South 500 West, Suite 101\nSalt Lake City, UT 84101'
                  }
                />
              </Row>
              {!hideAccums && (
                <>
                  <Row>
                    <SubTitle className="mt-5">Deductible & Max OOP</SubTitle>
                  </Row>
                  <Row>
                    <DataGrid>
                      <DataCell
                        title="INN Deductible (Ind)"
                        content={`${currencyFormatter.format(
                          deductible_individual_inn
                        )} / ${currencyFormatter.format(
                          max_deductible_individual_inn
                        )}`}
                      />
                      <DataCell
                        title="INN Deductible (Family)"
                        content={`${currencyFormatter.format(
                          deductible_family_inn
                        )} / ${currencyFormatter.format(
                          max_deductible_family_inn
                        )}`}
                      />
                      <DataCell
                        title="OON Deductible (Ind)"
                        content={`${currencyFormatter.format(
                          deductible_individual_oon
                        )} / ${currencyFormatter.format(
                          max_deductible_individual_oon
                        )}`}
                      />
                      <DataCell
                        title="OON Deductible (Family)"
                        content={`${currencyFormatter.format(
                          deductible_family_oon
                        )} / ${currencyFormatter.format(
                          max_deductible_family_oon
                        )}`}
                      />
                      <div
                        style={{
                          borderTop: '1px solid #E9ECEF',
                          gridColumn: '1/7'
                        }}
                      />
                      <DataCell
                        title="INN Max OOP (Ind)"
                        content={`${currencyFormatter.format(
                          oop_individual_inn
                        )} / ${currencyFormatter.format(
                          max_oop_individual_inn
                        )}`}
                      />
                      <DataCell
                        title="INN Max OOP (Family)"
                        content={`${currencyFormatter.format(
                          oop_family_inn
                        )} / ${currencyFormatter.format(max_oop_family_inn)}`}
                      />
                      <DataCell
                        title="OON Max OOP (Ind)"
                        content={`${currencyFormatter.format(
                          oop_individual_oon
                        )} / ${currencyFormatter.format(
                          max_oop_individual_oon
                        )}`}
                      />
                      <DataCell
                        title="OON Max OOP (Family)"
                        content={`${currencyFormatter.format(
                          oop_family_oon
                        )} / ${currencyFormatter.format(max_oop_family_oon)}`}
                      />
                    </DataGrid>
                  </Row>
                </>
              )}
              <Row>
                <SubTitle className="mt-5">
                  {eligibilityResult.plan?.is_hdhp
                    ? 'Copay/Coinsurance After Deductible (In-Network)'
                    : 'Copay/Coinsurance (In-Network)'}
                </SubTitle>
              </Row>
              <Row>
                <DataGrid>
                  <DataCell
                    title="Primary Care"
                    content={`$${
                      eligibilityResult.plan?.doctor_visit_inn_copay ?? 0
                    } / ${
                      eligibilityResult.plan?.doctor_visit_inn_coinsurance ?? 0
                    }%`}
                  />
                  <DataCell
                    title="Urgent Care"
                    content={`$${
                      eligibilityResult.plan?.urgent_care_inn_copay ?? 0
                    } / ${
                      eligibilityResult.plan?.urgent_care_inn_coinsurance ?? 0
                    }%`}
                  />
                  <DataCell
                    title="Specialist"
                    content={`$${
                      eligibilityResult.plan?.specialist_inn_copay ?? 0
                    } / ${
                      eligibilityResult.plan?.specialist_inn_coinsurance ?? 0
                    }%`}
                  />
                  <DataCell
                    title="Telehealth"
                    content={`$${
                      eligibilityResult.plan?.telehealth_inn_copay ?? 0
                    } / ${
                      eligibilityResult.plan?.telehealth_inn_coinsurance ?? 0
                    }%`}
                  />
                </DataGrid>
              </Row>
              <Row className="mb-4 mt-5">
                {!formatForPrint && (
                  <Button
                    href={benefitsUrl}
                    target="_blank"
                    rel="nofollow noreferrer noopener"
                  >
                    View Benefits
                  </Button>
                )}
              </Row>
              {/* <Row className="pt-5">
                <Accordion className="w-100">
                  <Accordion.Toggle as="div" role="button" eventKey="0">
                    <Row className="align-items-center ml-2">
                      <CaretIcon />
                      <SubTitle className="m-0">Benefits</SubTitle>
                    </Row>
                  </Accordion.Toggle>
                  <Accordion.Collapse eventKey="0">
                    <BenefitsSection benefits={benefits} />
                  </Accordion.Collapse>
                </Accordion>
              </Row> */}
            </>
          )}
          <Row className="border-top">
            <TicketForm 
              clearResults={clearResults}
              criteriaData={criteriaData}
              foundResult={true} />
          </Row>
        </Container>
      </div>
    )
  }
)
